import { useMemo } from "react";
import { Bell } from "lucide-react";
import { useApp } from "@/store/AppContext";
import { useAuth } from "@/store/AuthContext";
import {
  useGroup,
  useGroupCalendar,
  useGroupOverlap,
  useHabitMap,
  useMemberMap,
} from "@/lib/hooks";

const WEEK_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const HOURS = Array.from({ length: 19 }, (_, i) => i + 5);

function densityClass(v) {
  return ["bg-stone-50", "bg-sage/20", "bg-sage/40", "bg-sage/60", "bg-sage/80", "bg-sage"][v] || "bg-stone-50";
}

export default function CalendarPage() {
  const { scheduled, sendNudge } = useApp();
  const { user } = useAuth();
  const { data: group } = useGroup();
  const { data: calendar = [] } = useGroupCalendar();
  const { data: overlapDoc } = useGroupOverlap();
  const habitMap = useHabitMap();
  const memberMap = useMemberMap();

  const overlap = overlapDoc?.grid || [];

  const currentUserForCal = useMemo(
    () => ({
      id: user?.user_id || "u_demo",
      name: user?.name || "You",
      avatar: user?.picture,
      color: "#292524",
    }),
    [user]
  );

  const merged = useMemo(() => {
    const items = [...calendar];
    scheduled.forEach(({ template_id, start }) => {
      const hour = parseInt(start.split(":")[0], 10);
      for (let d = 0; d < 7; d++) {
        if (!items.some((it) => it.day === d && it.hour === hour && it.memberId === currentUserForCal.id)) {
          items.push({ day: d, hour, habitId: template_id, memberId: currentUserForCal.id });
        }
      }
    });
    return items;
  }, [calendar, scheduled, currentUserForCal.id]);

  const cellItems = (d, h) => merged.filter((it) => it.day === d && it.hour === h);

  const resolveMember = (id) => {
    if (id === currentUserForCal.id) return currentUserForCal;
    return memberMap[id];
  };

  return (
    <div className="space-y-8" data-testid="calendar-page">
      <header>
        <span className="eyebrow">Shared calendar · {group?.name || "your circle"}</span>
        <h1 className="font-serif text-4xl sm:text-5xl text-stone-900 mt-2 leading-tight">
          A single week. <span className="italic text-terracotta">Kept by many hands.</span>
        </h1>
        <p className="text-stone-600 mt-3 max-w-2xl leading-relaxed">
          Every colored square is a habit someone in your circle has scheduled. Denser cells mean more people are keeping the hour together. Tap a habit to send a nudge.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <span className="eyebrow">Density</span>
        {[0, 1, 2, 3, 4, 5].map((v) => (
          <span key={v} className={`w-6 h-3 rounded ${densityClass(v)}`} />
        ))}
        <span className="eyebrow">quiet → loud</span>
      </div>

      <div className="card-editorial overflow-hidden" data-testid="week-grid">
        <div className="overflow-x-auto">
          <div className="min-w-[720px]">
            <div className="grid grid-cols-[64px_repeat(7,1fr)] border-b border-stone-200">
              <div className="p-3" />
              {WEEK_DAYS.map((d) => (
                <div key={d} className="p-3 text-center eyebrow border-l border-stone-100">
                  {d}
                </div>
              ))}
            </div>
            {HOURS.map((h, hi) => (
              <div
                key={h}
                className="grid grid-cols-[64px_repeat(7,1fr)] border-b border-stone-100 last:border-b-0"
              >
                <div className="p-2 data-mono text-xs text-stone-500 text-right pr-3">
                  {String(h).padStart(2, "0")}:00
                </div>
                {WEEK_DAYS.map((_, di) => {
                  const level = overlap[di]?.[hi] ?? 0;
                  const items = cellItems(di, h);
                  return (
                    <div
                      key={di}
                      className={`relative border-l border-stone-100 min-h-[52px] ${densityClass(level)} p-1`}
                      data-testid={`cell-${di}-${h}`}
                    >
                      <div className="flex flex-wrap gap-1">
                        {items.slice(0, 3).map((it, idx) => {
                          const habit = habitMap[it.habitId];
                          const mem = resolveMember(it.memberId);
                          if (!habit || !mem) return null;
                          return (
                            <button
                              key={idx}
                              onClick={() =>
                                sendNudge({ recipient_id: mem.id, recipient_name: mem.name, habit_id: habit.id })
                              }
                              className="group inline-flex items-center gap-1 rounded-full bg-white/90 border border-stone-200 px-2 py-0.5 text-[10px] hover:bg-white"
                              style={{ color: mem.color || "#292524" }}
                              data-testid={`entry-${di}-${h}-${idx}`}
                              title={`${habit.title} · ${mem.name}`}
                            >
                              <span
                                className="w-1.5 h-1.5 rounded-full"
                                style={{ background: mem.color || "#292524" }}
                              />
                              {habit.title.length > 14 ? habit.title.slice(0, 12) + "…" : habit.title}
                              <Bell size={10} className="opacity-0 group-hover:opacity-100" />
                            </button>
                          );
                        })}
                        {items.length > 3 && (
                          <span className="text-[10px] text-stone-700 px-1">+{items.length - 3}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
